import {useEffect, useMemo, useRef, useState} from 'react'
import {RiverAccordion, useTheme, Text, Link, useWindowSize, Grid, Box} from '@primer/react-brand'
import {getOS, OS} from '@github-ui/get-os'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {clsx} from 'clsx'

import {SectionHeader} from '../SectionHeader/SectionHeader'
import type {Visual, VisualResolved} from '../../Index.types'

import Styles from './Features.module.css'
import {WebGLDither} from '@github-ui/brand-webgl'

export interface FeaturesProps {
  heading: string
  body: string
  items: Array<{
    heading: string
    body: string
    cta: {
      label: string
      href: string
    }
    visual: Visual
  }>
}

export const Features = ({heading, body, items}: FeaturesProps) => {
  const [isMounted, setIsMounted] = useState<boolean>(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const {isLarge: isDesktopView} = useWindowSize()
  const {colorMode} = useTheme()

  const featureItems = useMemo(() => {
    if (!isMounted) return []

    const os = getOS()
    const resolvedOS: OS = os.os === 'macOS' ? 'macOS' : 'Windows'
    const resolvedTheme =
      colorMode === 'auto'
        ? ssrSafeWindow?.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : colorMode

    return items.map(item => {
      const visual: VisualResolved = {
        desktop: {
          ...item.visual.desktop,
          src: item.visual.desktop.src[resolvedOS][resolvedTheme],
        },
        mobile: {
          ...item.visual.mobile,
          src: item.visual.mobile.src[resolvedOS][resolvedTheme],
        },
      }

      return {
        ...item,
        visual,
      }
    })
  }, [items, colorMode, isMounted])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMounted(true)

    if (!wrapperRef.current) return

    const riverAccordionVisuals = wrapperRef.current.querySelector(
      `.${Styles['Features__riverAccordion']} > *:nth-child(2)`,
    )

    if (!riverAccordionVisuals) return

    const originalBgNode = wrapperRef.current.querySelector(`:scope > .${Styles['Features__ditherBg']}`)
    if (!originalBgNode) return

    const moveDitherBg = () => {
      if (originalBgNode.parentElement === riverAccordionVisuals) return
      riverAccordionVisuals.appendChild(originalBgNode)
    }

    // Keep the dither background inside the RiverAccordion visuals container after re-renders
    const ditherBgObserver = new MutationObserver(entries => {
      for (const _ of entries) {
        moveDitherBg()
      }
    })

    ditherBgObserver.observe(riverAccordionVisuals, {childList: true})
    moveDitherBg()

    return () => {
      ditherBgObserver.disconnect()
    }
  }, [])

  return (
    <div ref={wrapperRef} className={Styles['Features']}>
      <SectionHeader heading={heading} body={body} />

      <Box className={clsx(Styles['Features__gridContent'], Styles['Features__gridContentWide'])}>
        <Grid columnGap="none" rowGap="none" enableGutters={false}>
          <Grid.Column span={12} className={Styles['Features__gridColumn']}>
            <RiverAccordion variant="gridline" className={Styles['Features__riverAccordion']}>
              {featureItems.map((item, index) => {
                const visual = item.visual[isDesktopView ? 'desktop' : 'mobile']
                return (
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  <RiverAccordion.Item key={`item-${index}`}>
                    <RiverAccordion.Heading>{item.heading}</RiverAccordion.Heading>
                    <RiverAccordion.Content>
                      <Text>{item.body}</Text>
                      <Link href={item.cta.href}>{item.cta.label}</Link>
                    </RiverAccordion.Content>

                    <RiverAccordion.Visual
                      className={clsx(
                        Styles['Features__visualWrapper'],
                        visual.position &&
                          visual.position !== 'center' &&
                          Styles[`Features__visualWrapper--${visual.position}`],
                      )}
                    >
                      {(item.visual.desktop.type === 'image' || item.visual.mobile.type === 'image') && (
                        <picture className={clsx(Styles['Features__visual'], Styles['Features__visual--image'])}>
                          {item.visual.mobile.type === 'image' && (
                            <source srcSet={item.visual.mobile.src} media="(max-width: 543px)" />
                          )}
                          {item.visual.desktop.type === 'image' && (
                            <source srcSet={item.visual.desktop.src} media="(min-width: 544px)" />
                          )}
                          <img
                            src={
                              item.visual.desktop.type === 'image' ? item.visual.desktop.src : item.visual.mobile.src
                            }
                            alt={
                              isDesktopView || item.visual.mobile.type !== 'image'
                                ? item.visual.desktop.alt
                                : item.visual.mobile.alt
                            }
                          />
                        </picture>
                      )}

                      {item.visual.desktop.type === 'video' && item.visual.mobile.type === 'video' && (
                        <video
                          className={clsx(Styles['Features__visual'], Styles['Features__visual--video'])}
                          autoPlay
                          loop
                          muted
                          playsInline
                          poster={item.visual.desktop.coverImageSrc}
                          aria-label={isDesktopView ? item.visual.desktop.alt : item.visual.mobile.alt}
                        >
                          <source src={item.visual.mobile.src} media="(max-width: 543px)" />
                          <source src={item.visual.desktop.src} media="(min-width: 544px)" />
                        </video>
                      )}
                    </RiverAccordion.Visual>
                  </RiverAccordion.Item>
                )
              })}
            </RiverAccordion>
          </Grid.Column>
        </Grid>
      </Box>

      <WebGLDither
        className={Styles['Features__ditherBg']}
        params={{color: 'pearl', type: 'noise', fadeIn: true, dotOpacity: 0.2}}
      />
    </div>
  )
}
